import React, { useEffect, useMemo, useState, Suspense } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import {
  Settings,
  Download,
  Moon,
  Sun,
  Shield,
  AlertTriangle,
  Activity,
  Users,
  TrendingUp,
  Scan,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  dashboardApi,
  homeApi,
  metricsApi,
  activityApi,
} from "../services/api";
import scanService from "../services/scanService";
import {
  fetchAssetsListForHome,
  fetchSuppliersListForHome,
} from "../mocks/dashboardFetch";
import { API_ENDPOINTS } from "../config/api";
import { featureFlags } from "../config/featureFlags";
import { fetchEffectiveStandardAccess, type EffectiveStandardAccess } from "../services/standardAccessApi";
import { normalizeComplianceStandardCode } from "../utils/complianceStandard";
import OperationalWorkflowButton from "../components/compliance-workflow/OperationalWorkflowButton";
import {
  buildHomeComplianceFromSnapshots,
  formatSelfVerifiedSubtitle,
} from "../utils/homeComplianceDisplay";
import { currentOrgView, getActiveOrgId, isCurrentOrgView } from "../utils/orgUtils";
import { safeJson } from "../lib/utils";

function unwrapStarterGuideDto(json: unknown): Record<string, unknown> | null {
  if (!json || typeof json !== "object") return null;
  const envelope = json as Record<string, unknown>;
  if (envelope.data && typeof envelope.data === "object") {
    return envelope.data as Record<string, unknown>;
  }
  return envelope;
}
import { useAuth } from "../components/AuthProvider";
import { useI18n } from "../i18n";
import { useRestrictedVisibility } from "../hooks/useRestrictedVisibility";
import HomeExecutiveGovernanceSection from "../components/executive-governance/HomeExecutiveGovernanceSection";
import HomeEnterpriseOperationalCenter from "../components/enterprise-operational/HomeEnterpriseOperationalCenter";
import HomeOrganizationAssetsPanel from "../components/home/HomeOrganizationAssetsPanel";
import { useCurrentVulnerabilityPosture } from "../hooks/useCurrentVulnerabilityPosture";
import HomeComplianceTrendPanel from "../components/home/HomeComplianceTrendPanel";
import HomeExecutivePostureWidgetsRow from "../components/home/executive-posture/HomeExecutivePostureWidgetsRow";
import HomeSuppliersExecutiveCard from "../components/home/HomeSuppliersExecutiveCard";

import {
  getStoredNationalProfile,
  getStandardsForProfile,
} from "../components/NationalProfileSelector";
import { SecurityOverviewCard } from "../components/dashboard/SecurityOverviewCard";
import { PageLayout } from "../layouts/PageLayout";
import { HomeDataRefreshedMeta } from "../components/layout/HomeDataRefreshedMeta";
import { Button } from "../components/ui/Button";
import { SecurityCard } from "../components/ui/SecurityCard";
import { ProblemCard } from "../components/ui/ProblemCard";
import { uiScope } from "../lib/cssScope";
import assetsMetricsGrid from "../styles/components/home/AssetsMetricsGrid.module.css";
import styles from "../styles/pages/home/Home.module.scss";
import {
  formatSignedDelta,
  getSeverityDelta,
  sumProblemClassCounts,
  getProblemsBreakdown,
} from "../lib/assetsMonitoring";
import { resolveHomeVulnerabilityKpis } from "../lib/homeVulnerabilityKpis";

import { fetchInformationSystems } from "../services/informationSystemsApi";
import { useNavigate } from "react-router-dom";
import type { Recommendation } from "../utils/recommendationsTypes";

import { lazyWithReload } from "../utils/lazyWithReload";

// Lazy load RecommendationsPanel to avoid circular dependencies
const LazyRecommendationsPanel = lazyWithReload(
  () => import("../components/recommendations/RecommendationsPanel")
);

const COLORS = ["#56a3d9", "#10b981", "#f59e0b", "#ef4444", "#3b82f6"];

/** Grid area classes for Regulatory Compliance → Assets (AssetsMetricsGrid.module.css). */
const ASSETS_METRIC_GRID_CELL: Record<string, string> = {
  assets: assetsMetricsGrid.summary,
  critical: assetsMetricsGrid.critical,
  high: assetsMetricsGrid.high,
  medium: assetsMetricsGrid.medium,
  low: assetsMetricsGrid.low,
};

/** Returns a string slug for org (id or name). Never returns "[object Object]" or non-string. */
function getOrgSlug(org: unknown): string {
  if (org == null) return "";
  if (typeof org === "string") {
    const s = org.trim();
    return s === "" || s === "[object Object]" ? "" : s;
  }
  if (typeof org === "object" && org !== null) {
    const o = org as Record<string, unknown>;
    const id = o.id != null ? String(o.id).trim() : "";
    const name = o.name != null ? String(o.name).trim() : "";
    const slug = id || name;
    return slug === "" || slug === "[object Object]" ? "" : slug;
  }
  return "";
}

const VULNERABILITY_SEVERITIES = [
  "critical",
  "high",
  "medium",
  "low",
  "info",
] as const;
type VulnerabilitySeverity = (typeof VULNERABILITY_SEVERITIES)[number];

const Home: React.FC = () => {
  const { user, token } = useAuth();
  const { restrictedMode } = useRestrictedVisibility();
  const { t } = useI18n();
  const navigate = useNavigate();

  // i18n fallback helper (alias for t with fallback support)
  const tr = (
    primaryKey: string,
    fallbackKey?: string,
    plainFallback?: string
  ) => {
    const v = t(primaryKey, fallbackKey);
    if (v !== primaryKey) return v;
    return plainFallback || primaryKey;
  };
  const [theme, setTheme] = useState<"light" | "dark">(
    (localStorage.getItem("theme") as "light" | "dark") || "light"
  );

  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState<any>(null);
  const [compliance, setCompliance] = useState<any[]>([]);
  const [securityHealth, setSecurityHealth] = useState<any>(null);
  const [assetsMonitoring, setAssetsMonitoring] = useState<any>(null);
  const { posture: vulnerabilityPosture } = useCurrentVulnerabilityPosture(Boolean(token));
  const [assetsList, setAssetsList] = useState<any[]>([]);
  const [frameworkSeries, setFrameworkSeries] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any>(null);
  const [customers, setCustomers] = useState<any>(null);
  const [supplierRecords, setSupplierRecords] = useState<any[]>([]);
  const [customerTrustRecords, setCustomerTrustRecords] = useState<any[]>([]);
  const [testOwners, setTestOwners] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedStandards, setSelectedStandards] = useState<Set<string>>(
    new Set()
  );
  const [homeEffectiveAccess, setHomeEffectiveAccess] = useState<EffectiveStandardAccess | null>(null);
  const [scanJobs, setScanJobs] = useState<any[]>([]);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetModalLoading, setAssetModalLoading] = useState(false);
  const [assetModalError, setAssetModalError] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedAssetSummary, setSelectedAssetSummary] = useState<any | null>(
    null
  );
  const [assetDetailsCache, setAssetDetailsCache] = useState<
    Record<string, any>
  >({});
  const [homeRecommendations, setHomeRecommendations] = useState<
    Recommendation[]
  >([]);

  const organizationParam = useMemo(() => getActiveOrgId(), [user]);

  const severityColorMap: Record<VulnerabilitySeverity, string> = {
    critical: styles.severityCritical,
    high: styles.severityHigh,
    medium: styles.severityMedium,
    low: styles.severityLow,
    info: styles.severityInfo,
  };

  const normalizeAssetsPayload = (payload: any): any[] => {
    const extractArray = (value: any): any[] => {
      if (Array.isArray(value)) return value;
      if (Array.isArray(value?.data)) return value.data;
      if (Array.isArray(value?.items)) return value.items;
      if (Array.isArray(value?.list)) return value.list;
      return [];
    };

    return extractArray(payload).map((row: any, idx: number) => {
      const vulnerabilitiesRoot =
        row?.vulnerabilities ||
        row?.vulnerabilityCounts ||
        row?.vulnerability_counts ||
        row?.stats ||
        row?.summary ||
        {};
      const vulnerabilitiesArray = Array.isArray(row?.vulnerabilities)
        ? row.vulnerabilities
        : Array.isArray(row?.vulnerabilities?.items)
        ? row.vulnerabilities.items
        : Array.isArray(row?.findings)
        ? row.findings
        : Array.isArray(row?.issues)
        ? row.issues
        : Array.isArray(row?.results)
        ? row.results
        : Array.isArray(row?.vulnerabilityDetails)
        ? row.vulnerabilityDetails
        : [];

      const derivedCounts: Record<VulnerabilitySeverity, number> = {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
      };

      vulnerabilitiesArray.forEach((item: any) => {
        const severityRaw = (
          item?.severity ||
          item?.level ||
          item?.risk ||
          item?.priority ||
          ""
        )
          .toString()
          .toLowerCase();
        const normalized =
          severityRaw === "informational" ? "info" : severityRaw;
        if (
          (VULNERABILITY_SEVERITIES as readonly string[]).includes(normalized)
        ) {
          derivedCounts[normalized as VulnerabilitySeverity] += 1;
        }
      });

      const candidateSources = [
        row,
        row?.vulnerabilities,
        row?.vulnerabilityCounts,
        row?.vulnerability_counts,
        row?.vulnerabilities?.summary,
        row?.stats,
        row?.summary,
        row?.metrics,
        row?.vulnerabilitySummary,
        row?.severityCounts,
        row?.counts,
        vulnerabilitiesRoot,
      ].filter(Boolean);

      const resolveCount = (sev: VulnerabilitySeverity) => {
        const keyVariants = [
          sev,
          sev.toUpperCase(),
          `${sev}Count`,
          `${sev}_count`,
          `${sev}Issues`,
          `${sev}_issues`,
          `${sev}Total`,
          `${sev}_total`,
        ];
        if (sev === "info") {
          keyVariants.push(
            "informational",
            "informationalCount",
            "informational_count",
            "informationalIssues",
            "informational_total",
            "informationalTotal",
            "infoCount"
          );
        }

        let best = derivedCounts[sev] || 0;

        const recordNumeric = (value: any) => {
          const num = Number(value);
          if (!Number.isNaN(num) && num >= 0) {
            best = Math.max(best, num);
          }
        };

        candidateSources.forEach((source) => {
          keyVariants.forEach((variant) => {
            if (
              source &&
              Object.prototype.hasOwnProperty.call(source, variant)
            ) {
              recordNumeric((source as any)[variant]);
            }
          });
          if (source && typeof source === "object" && !Array.isArray(source)) {
            Object.entries(source as Record<string, any>).forEach(
              ([key, value]) => {
                if (typeof value === "number" || typeof value === "string") {
                  const normalizedKey = key.toLowerCase();
                  const matches =
                    sev === "info"
                      ? normalizedKey.includes("info") ||
                        normalizedKey.includes("inform")
                      : normalizedKey.includes(sev);
                  if (matches) {
                    recordNumeric(value);
                  }
                }
              }
            );
          }
        });

        return best;
      };

      const counts: Record<VulnerabilitySeverity, number> = {
        critical: resolveCount("critical"),
        high: resolveCount("high"),
        medium: resolveCount("medium"),
        low: resolveCount("low"),
        info: resolveCount("info"),
      };

      const resolvedName =
        row?.name ||
        row?.assetName ||
        row?.hostname ||
        row?.url ||
        row?.ip ||
        row?.ipAddress ||
        `Asset ${idx + 1}`;
      const resolvedId =
        row?.id ||
        row?._id ||
        row?.assetId ||
        row?.uuid ||
        row?.external_id ||
        row?.slug ||
        resolvedName ||
        `asset-${idx}`;

      return {
        ...row,
        id: String(resolvedId),
        name: resolvedName,
        critical: counts.critical,
        high: counts.high,
        medium: counts.medium,
        low: counts.low,
        info: counts.info,
        severityCounts: counts,
        totalVulnerabilities: VULNERABILITY_SEVERITIES.reduce(
          (sum, sev) => sum + counts[sev],
          0
        ),
      };
    });
  };

  // Simple data validator to guard against incomplete API responses
  const validateData = (data: any, fields: string[], label: string) => {
    if (!data || typeof data !== "object") {
      console.warn(`⚠️ Missing ${label} data`);
      return false;
    }
    const missing = fields.filter((f) => data[f] === undefined);
    if (missing.length) {
      console.warn(`⚠️ ${label} missing fields:`, missing);
      return false;
    }
    return true;
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toHomeFrameworkName = (code: string): string => {
    const u = String(code || "").toUpperCase();
    if (u === "ISO27001" || u === "ISO_27001") return "ISO 27001";
    if (u === "ISO42001" || u === "ISO_42001" || u === "ISO 42001" || u === "ISO/IEC 42001" || u === "ISO/IEC 42001:2023") return "ISO/IEC 42001:2023";
    if (u === "SOC2" || u === "SOC_2" || u === "SOC 2") return "SOC2";
    if (u === "NIS2" || u === "NIS2_FULL") return "NIS2";
    if (u === "PCI_DSS_2025") return "PCI DSS 2025";
    if (u === "SWIFT_CSCF_2025") return "SWIFT CSCF 2025";
    if (u === "TECH_CONTROLS") return "Technical Controls";
    if (u === "SDLC_SECURITY") return "SDLC Security";
    return code;
  };

  const buildFrameworkSeriesFromUnified = (snapshots: any[]) => {
    const today = new Date();
    const points = Array.from({ length: 30 }, (_, i) => {
      const d = new Date(today.getTime() - (29 - i) * 24 * 60 * 60 * 1000);
      return d.toISOString().slice(0, 10);
    });
    return snapshots.map((snapshot: any) => {
      const hasSelf =
        snapshot?.metadata?.hasExtended ||
        snapshot?.metadata?.hasLegacy ||
        Number(snapshot?.selfAssessmentScore) > 0;
      const value = Number(
        hasSelf ? snapshot?.selfAssessmentScore : snapshot?.verifiedScore ?? 0
      );
      return {
        framework: toHomeFrameworkName(snapshot?.standardCode || ""),
        data: points.map((date) => ({ date, value })),
      };
    });
  };

  const buildComplianceFromUnified = (snapshots: any[]) =>
    buildHomeComplianceFromSnapshots(
      snapshots,
      toHomeFrameworkName,
      (key, fallback) => t(key, fallback),
    );

  useEffect(() => {
    const normalizeStandardName = (name: string): string => {
      if (!name) return "";
      const n = name.trim();
      if (n === "ISO/IEC 42001:2023" || n === "ISO/IEC 42001" || n.toUpperCase() === "ISO 42001" || n.toUpperCase() === "ISO42001" || n.toUpperCase() === "ISO_42001")
        return "ISO/IEC 42001:2023";
      if (n === "ISO/IEC 27001" || n.toUpperCase() === "ISO 27001")
        return "ISO 27001";
      if (n.toUpperCase() === "PCI DSS") return "PCI-DSS";
      if (n.toUpperCase() === "PCI DSS 2025") return "PCI DSS 2025";
      if (n.toUpperCase() === "SWIFT CSCF 2025") return "SWIFT CSCF 2025";
      if (n.toUpperCase() === "SOC 2" || n.toUpperCase() === "SOC2")
        return "SOC2";
      if (n.toUpperCase() === "GDPR") return "GDPR";
      if (n.toUpperCase() === "NIS2") return "NIS2";
      if (n.toUpperCase() === "DORA") return "DORA";
      return n;
    };

    const loadSelectedStandards = async (): Promise<Set<string>> => {
      try {
        const res = await fetch(API_ENDPOINTS.STARTER_GUIDE, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (res.ok) {
          const json = await safeJson(res);
          const dto = unwrapStarterGuideDto(json);
          if (dto) {
            const stds: string[] = Array.isArray(dto.relevantStandards)
              ? (dto.relevantStandards as string[])
              : [];
            return new Set(stds.map(normalizeStandardName));
          }
        }
        if (res.status === 404) {
          return new Set();
        }
        if (res.status === 401 || res.status === 403) {
          return new Set();
        }
      } catch {}
      try {
        const regRes = await fetch(API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (regRes.ok) {
          const body = await safeJson(regRes);
          const payload = body?.data ?? body;
          const codes: string[] = Array.isArray(payload?.enabledStandards)
            ? payload.enabledStandards
            : [];
          if (codes.length >= 0) {
            return new Set(codes.map(normalizeStandardName));
          }
        }
      } catch {}
      return new Set();
    };

    const normalizeForFilter = (n: string): string => {
      const u = (n || "").toUpperCase();
      if (u === "SOC 2" || u === "SOC2") return "SOC 2";
      if (u === "ISO/IEC 42001" || u === "ISO 42001" || u === "ISO42001" || u === "ISO_42001" || u === "ISO/IEC 42001:2023")
        return "ISO/IEC 42001:2023";
      if (u === "ISO/IEC 27001" || u === "ISO 27001" || u === "ISO27001")
        return "ISO 27001";
      if (u === "NIS2" || u === "NIS 2") return "NIS 2";
      return (n || "").trim();
    };
    const filterComplianceBySelected = (rows: any[], selected: Set<string>) => {
      if (!rows || rows.length === 0) return [];
      if (!selected || selected.size === 0) return [];
      return rows.filter((r: any) => {
        const name = String(r?.name || "").trim();
        return selected.has(normalizeForFilter(name)) || selected.has(name);
      });
    };

    const filterFrameworkSeriesBySelected = (
      series: any[],
      selected: Set<string>
    ) => {
      if (!series || series.length === 0) return [];
      if (!selected || selected.size === 0) return [];
      return series.filter((s: any) => {
        const fw = String(s?.framework || "").trim();
        return selected.has(normalizeForFilter(fw)) || selected.has(fw);
      });
    };

    const loadData = async () => {
      const viewTicket = currentOrgView();
      try {
        console.log("[Home] ⏳ Parallel loading...");

        const [
          incidentsRes,
          securityRes,
          assetsRes,
          complianceRes,
          metricsRes,
          unifiedSnapshotsRes,
          activityRes,
          supsRes,
          custsRes,
          supsListRes,
          assetsListRes,
        ] = await Promise.all([
          dashboardApi.getIncidents(),
          homeApi.getSecurityHealth(),
          homeApi.getAssetsMonitoring(),
          dashboardApi.getCompliance(),
          metricsApi.getFrameworkMetrics(),
          homeApi.getUnifiedAssessmentSnapshots(),
          activityApi.getTestOwnersActivity(),
          dashboardApi.getSuppliers(),
          dashboardApi.getCustomers(),
          fetchSuppliersListForHome(),
          fetchAssetsListForHome(organizationParam),
        ]);

        if (!isCurrentOrgView(viewTicket)) return;
        const selected = await loadSelectedStandards();
        const profileStandards = getStandardsForProfile(
          getStoredNationalProfile()
        );
        const merged = new Set([...selected, ...profileStandards]);
        setSelectedStandards(merged);

        let unifiedStandards = Array.isArray(unifiedSnapshotsRes?.standards)
          ? unifiedSnapshotsRes.standards
          : [];

        if (featureFlags.STANDARD_ACCESS_POLICY_V1) {
          const orgId = getActiveOrgId();
          if (orgId) {
            try {
              const effective = await fetchEffectiveStandardAccess(orgId);
              setHomeEffectiveAccess(effective);
              const granted = new Set(
                (effective.standardCodes || []).map((c) => c.toUpperCase()),
              );
              if (granted.size > 0) {
                unifiedStandards = unifiedStandards.filter((s: { standardCode?: string }) =>
                  granted.has(String(s.standardCode || "").toUpperCase()),
                );
              }
            } catch {
              /* legacy fallback — show all snapshots */
            }
          }
        }

        const complianceSource =
          unifiedStandards.length > 0
            ? buildComplianceFromUnified(unifiedStandards)
            : complianceRes.standards || [];
        const frameworkSource =
          unifiedStandards.length > 0
            ? buildFrameworkSeriesFromUnified(unifiedStandards)
            : (Array.isArray(metricsRes.data) ? metricsRes.data : []);

        const complianceFiltered = filterComplianceBySelected(complianceSource, selected);
        const frameworksFiltered = filterFrameworkSeriesBySelected(frameworkSource, selected);

        if (!isCurrentOrgView(viewTicket)) return;
        setIncidents(incidentsRes);
        if (
          validateData(securityRes, ["lastScan"], "SecurityHealth")
        ) {
          setSecurityHealth(securityRes);
        }
        if (
          validateData(
            assetsRes,
            ["critical", "high", "medium", "low", "total"],
            "AssetsMonitoring"
          )
        ) {
          let informationSystemsCount = Number(
            (assetsRes as { informationSystemsCount?: number })?.informationSystemsCount ?? 0
          )
          if (token && organizationParam) {
            try {
              const systems = await fetchInformationSystems(token, organizationParam)
              if (Array.isArray(systems) && systems.length > informationSystemsCount) {
                informationSystemsCount = systems.length
              }
            } catch {
              /* keep API count */
            }
          }
          if (!isCurrentOrgView(viewTicket)) return;
          setAssetsMonitoring({
            ...assetsRes,
            informationSystemsCount,
          })
        }
        setCompliance(complianceFiltered || []);
        setFrameworkSeries(frameworksFiltered || []);

        // Home recommendations block removed per compliance unification
        setHomeRecommendations([]);

        // Log framework series data for debugging
        console.log("[Home] Framework series data:", {
          raw: metricsRes.data,
          filtered: frameworksFiltered,
          length: frameworksFiltered?.length || 0,
          firstSeries: frameworksFiltered?.[0],
        });
        setTestOwners(
          Object.entries(activityRes.data || {}).map(([owner, arr]: any) => ({
            owner,
            findings: arr.reduce(
              (sum: number, it: any) => sum + it.findings_high,
              0
            ),
          }))
        );
        setSuppliers(supsRes);
        setCustomers(custsRes);
        try {
          const sl =
            supsListRes && (supsListRes as Response).ok
              ? await (supsListRes as Response).json()
              : null;
          setSupplierRecords(Array.isArray(sl?.suppliers) ? sl.suppliers : []);
        } catch {}
        // Customer Trust detailed list is optional in this server flavor; keep empty if unavailable
        setCustomerTrustRecords([]);
        try {
          if (assetsListRes && (assetsListRes as Response).ok) {
            const assetsPayload = await safeJson(assetsListRes as Response);
            if (!isCurrentOrgView(viewTicket)) return;
            setAssetsList(normalizeAssetsPayload(assetsPayload));
          }
        } catch (assetsErr) {
          console.warn(
            "[Home] Failed to load assets list for dashboard:",
            assetsErr
          );
        }
      } catch (err) {
        console.error("[Home] ❌ Error loading data:", err);
        if (isCurrentOrgView(viewTicket)) setError("Error loading dashboard data");
      } finally {
        if (isCurrentOrgView(viewTicket)) setLoading(false);
      }
    };

    loadData();
    (async () => {
      try {
        const rawUser = localStorage.getItem("user");
        const userObj = rawUser ? JSON.parse(rawUser) : null;
        const orgId = getActiveOrgId();
        const scans = await scanService.providerListScans(orgId ?? undefined);
        setScanJobs(scans || []);
      } catch {}
    })();
  }, [organizationParam]);

  // Auto-poll scans status (every 3 minutes)
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const rawUser = localStorage.getItem("user");
        const userObj = rawUser ? JSON.parse(rawUser) : null;
        const orgId = getActiveOrgId();
        const scans = await scanService.providerListScans(orgId ?? undefined);
        setScanJobs(scans || []);
      } catch {}
    }, 180000);
    return () => clearInterval(id);
  }, []);

  // Refresh Home charts when compliance is updated elsewhere
  useEffect(() => {
    const normalizeStandardName = (name: string): string => {
      if (!name) return "";
      const n = name.trim();
      if (n === "ISO/IEC 42001:2023" || n === "ISO/IEC 42001" || n.toUpperCase() === "ISO 42001" || n.toUpperCase() === "ISO42001" || n.toUpperCase() === "ISO_42001")
        return "ISO/IEC 42001:2023";
      if (n === "ISO/IEC 27001" || n.toUpperCase() === "ISO 27001")
        return "ISO 27001";
      if (n.toUpperCase() === "PCI DSS") return "PCI-DSS";
      if (n.toUpperCase() === "PCI DSS 2025") return "PCI DSS 2025";
      if (n.toUpperCase() === "SWIFT CSCF 2025") return "SWIFT CSCF 2025";
      if (n.toUpperCase() === "SOC 2" || n.toUpperCase() === "SOC2")
        return "SOC2";
      if (n.toUpperCase() === "GDPR") return "GDPR";
      if (n.toUpperCase() === "NIS2") return "NIS2";
      if (n.toUpperCase() === "DORA") return "DORA";
      return n;
    };

    const loadSelectedStandards = async (): Promise<Set<string>> => {
      try {
        const res = await fetch(API_ENDPOINTS.STARTER_GUIDE, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (res.ok) {
          const json = await safeJson(res);
          const dto = unwrapStarterGuideDto(json);
          if (dto) {
            const stds: string[] = Array.isArray(dto.relevantStandards)
              ? (dto.relevantStandards as string[])
              : [];
            return new Set(stds.map(normalizeStandardName));
          }
        }
        if (res.status === 404) {
          return new Set();
        }
        if (res.status === 401 || res.status === 403) {
          return new Set();
        }
      } catch {}
      try {
        const regRes = await fetch(API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (regRes.ok) {
          const body = await safeJson(regRes);
          const payload = body?.data ?? body;
          const codes: string[] = Array.isArray(payload?.enabledStandards)
            ? payload.enabledStandards
            : [];
          return new Set(codes.map(normalizeStandardName));
        }
      } catch {}
      return new Set();
    };

    const normalizeForFilterRefresh = (n: string): string => {
      const u = (n || "").toUpperCase();
      if (u === "SOC 2" || u === "SOC2") return "SOC 2";
      if (u === "ISO/IEC 42001" || u === "ISO 42001" || u === "ISO42001" || u === "ISO_42001" || u === "ISO/IEC 42001:2023")
        return "ISO/IEC 42001:2023";
      if (u === "ISO/IEC 27001" || u === "ISO 27001" || u === "ISO27001")
        return "ISO 27001";
      if (u === "NIS2" || u === "NIS 2") return "NIS 2";
      return (n || "").trim();
    };
    const filterComplianceBySelected = (rows: any[], selected: Set<string>) => {
      if (!rows || rows.length === 0) return [];
      if (!selected || selected.size === 0) return [];
      return rows.filter((r: any) => {
        const name = String(r?.name || "").trim();
        return (
          selected.has(normalizeForFilterRefresh(name)) || selected.has(name)
        );
      });
    };

    const filterFrameworkSeriesBySelected = (
      series: any[],
      selected: Set<string>
    ) => {
      if (!series || series.length === 0) return [];
      if (!selected || selected.size === 0) return [];
      return series.filter((s: any) => {
        const fw = String(s?.framework || "").trim();
        return selected.has(normalizeForFilterRefresh(fw)) || selected.has(fw);
      });
    };

    const onComplianceUpdated = () => {
      (async () => {
        const viewTicket = currentOrgView();
        try {
          const [
            incidentsRes,
            securityRes,
            assetsRes,
            complianceRes,
            metricsRes,
            unifiedSnapshotsRes,
            activityRes,
            supsRes,
            custsRes,
            assetsListRes,
          ] = await Promise.all([
            dashboardApi.getIncidents(),
            homeApi.getSecurityHealth(),
            homeApi.getAssetsMonitoring(),
            dashboardApi.getCompliance(),
            metricsApi.getFrameworkMetrics(),
            homeApi.getUnifiedAssessmentSnapshots(),
            activityApi.getTestOwnersActivity(),
            dashboardApi.getSuppliers(),
            dashboardApi.getCustomers(),
            fetchAssetsListForHome(organizationParam),
          ]);

          if (!isCurrentOrgView(viewTicket)) return;
          const selected = await loadSelectedStandards();
          const profileStandards = getStandardsForProfile(
            getStoredNationalProfile()
          );
          const merged = new Set([...selected, ...profileStandards]);
          setSelectedStandards(merged);

          if (!isCurrentOrgView(viewTicket)) return;
        setIncidents(incidentsRes);
          if (
            validateData(securityRes, ["lastScan"], "SecurityHealth")
          ) {
            setSecurityHealth(securityRes);
          }
          if (
            validateData(
              assetsRes,
              ["critical", "high", "medium", "low", "total"],
              "AssetsMonitoring"
            )
          ) {
            let informationSystemsCount = Number(
              (assetsRes as { informationSystemsCount?: number })?.informationSystemsCount ?? 0
            )
            if (token && organizationParam) {
              try {
                const systems = await fetchInformationSystems(token, organizationParam)
                if (Array.isArray(systems) && systems.length > informationSystemsCount) {
                  informationSystemsCount = systems.length
                }
              } catch {
                /* keep API count */
              }
            }
            if (!isCurrentOrgView(viewTicket)) return;
            setAssetsMonitoring({
              ...assetsRes,
              informationSystemsCount,
            })
          }
          let unifiedStandardsRefresh = Array.isArray(unifiedSnapshotsRes?.standards)
            ? unifiedSnapshotsRes.standards
            : [];
          if (featureFlags.STANDARD_ACCESS_POLICY_V1) {
            const orgId = getActiveOrgId();
            if (orgId) {
              try {
                const effective = await fetchEffectiveStandardAccess(orgId);
                setHomeEffectiveAccess(effective);
                const granted = new Set(
                  (effective.standardCodes || []).map((c) => c.toUpperCase()),
                );
                if (granted.size > 0) {
                  unifiedStandardsRefresh = unifiedStandardsRefresh.filter(
                    (s: { standardCode?: string }) =>
                      granted.has(String(s.standardCode || "").toUpperCase()),
                  );
                }
              } catch {
                /* legacy fallback */
              }
            }
          }
          const complianceSource =
            unifiedStandardsRefresh.length > 0
              ? buildComplianceFromUnified(unifiedStandardsRefresh)
              : complianceRes.standards || [];
          const frameworkSource =
            unifiedStandardsRefresh.length > 0
              ? buildFrameworkSeriesFromUnified(unifiedStandardsRefresh)
              : (Array.isArray(metricsRes.data) ? metricsRes.data : []);
          const filteredCompliance = filterComplianceBySelected(complianceSource, selected);
          setCompliance(filteredCompliance || []);

          // Home recommendations block removed per compliance unification
          setHomeRecommendations([]);
          setFrameworkSeries(
            filterFrameworkSeriesBySelected(frameworkSource, selected)
          );
          setTestOwners(
            Object.entries(activityRes.data || {}).map(([owner, arr]: any) => ({
              owner,
              findings: arr.reduce(
                (sum: number, it: any) => sum + it.findings_high,
                0
              ),
            }))
          );
          setSuppliers(supsRes);
          setCustomers(custsRes);
          try {
            if (assetsListRes && (assetsListRes as Response).ok) {
              const assetsPayload = await safeJson(assetsListRes as Response);
              if (!isCurrentOrgView(viewTicket)) return;
              setAssetsList(normalizeAssetsPayload(assetsPayload));
            }
          } catch (e) {
            console.error("[Home] refresh assets list failed:", e);
          }
        } catch (e) {
          console.error("[Home] refresh after compliance-updated failed:", e);
        }
      })();
    };

    window.addEventListener("compliance-updated", onComplianceUpdated as any);
    return () =>
      window.removeEventListener(
        "compliance-updated",
        onComplianceUpdated as any
      );
  }, [organizationParam]);

  // Derived UI data and guards (must be before any early returns to keep hooks order stable)
  const safeArray = (a: any) => (Array.isArray(a) ? a : []);
  // SEC-004.3F: vulnerability health is independent from compliance readiness average
  const healthShowPercent = securityHealth?.showPercent !== false && securityHealth?.percent != null;
  const health = healthShowPercent ? Number(securityHealth?.percent ?? 0) : null;
  const healthAssessmentStatus = String(securityHealth?.assessmentStatus || "");
  const healthStatusText =
    securityHealth?.status ||
    (healthAssessmentStatus === "NOT_ASSESSED"
      ? "Not assessed"
      : healthAssessmentStatus === "ERROR"
        ? "Unknown / Data unavailable"
        : null);
  const healthWarning = securityHealth?.assessmentWarning || null;
  const lastImprovedStr = securityHealth?.lastImproved ? new Date(securityHealth.lastImproved).toLocaleDateString() : "—";
  const complianceStandards = safeArray(compliance);
  // Compliance readiness average — NOT mixed into vulnerability Security Health (SEC-004.3F)
  const computedHealthFromStandards = useMemo(() => {
    try {
      // Only average standards that are actually assessed (null percent = not assessed)
      const rows = safeArray(compliance).filter(
        (s: any) =>
          s?.percent != null &&
          Number.isFinite(Number(s.percent)) &&
          s?.assessmentDisplay !== "not_assessed" &&
          s?.assessmentDisplay !== "not_applicable",
      );
      if (rows.length === 0) return null;
      const avg =
        rows.reduce((sum: number, s: any) => sum + Number(s?.percent ?? 0), 0) /
        rows.length;
      return Math.round(avg);
    } catch {
      return null;
    }
  }, [compliance]);
  const healthFinal = healthShowPercent ? Number(health ?? 0) : 0;
  const healthDisplayPercent = healthShowPercent ? healthFinal : 0;
  // Use assetsList.length for accurate count — never assetsMonitoring.total (that is unique findings)
  const assetsTotal =
    assetsList.length > 0
      ? assetsList.length
      : Number((assetsMonitoring as any)?.assetsCount ?? 0);
  const assets = {
    total: assetsTotal,
    delta: Number((assetsMonitoring as any)?.delta ?? 0) || 0,
  };

  const assetsMonitoringRaw = assetsMonitoring as
    | Record<string, unknown>
    | undefined;

  const homeVulnKpis = useMemo(
    () =>
      resolveHomeVulnerabilityKpis({
        posture: vulnerabilityPosture as Record<string, unknown> | null,
        monitoring: assetsMonitoringRaw as Record<string, unknown> | null,
        securityHealth: securityHealth as Record<string, unknown> | null,
        assetsListCount: assetsList.length,
        risksFallback: getProblemsBreakdown(assetsMonitoringRaw).risks,
        controlsFallback: getProblemsBreakdown(assetsMonitoringRaw).controls,
      }),
    [vulnerabilityPosture, assetsMonitoringRaw, securityHealth, assetsList.length],
  );

  const scanSummaryMetricCards = useMemo(() => {
    const am = assetsMonitoringRaw;
    const problems = homeVulnKpis.problems;
    const isCount =
      homeVulnKpis.informationSystemsCount ||
      Number((am as Record<string, unknown>)?.informationSystemsCount ?? 0) ||
      0;
    const assetsCount =
      homeVulnKpis.assetsTotal > 0 ? homeVulnKpis.assetsTotal : assets.total;
    /** Tooltip on hover: signed delta + "since last scan" (no footer line on card). */
    const hoverDelta = (delta: number) =>
      t("home.metrics.hoverDeltaSinceScan", "{delta} since last scan", {
        delta: formatSignedDelta(delta),
      });

    const problemsSubline = t(
      "home.metrics.problemsBreakdownFailed",
      "{vuln} vulnerabilities · {risks} risks · {failed} failed controls",
      {
        vuln: String(problems.vulnerabilities),
        risks: String(problems.risks),
        failed: String(problems.controls),
      }
    );

    const portfolioHover = t(
      "home.metrics.portfolioHoverDetail",
      "Information systems: {is}. Assets: {assets}. Open problems: {problems} ({vuln} vulnerabilities, {risks} risks, {controls} control issues). Change since last scan: {delta}.",
      {
        is: String(isCount),
        assets: String(assetsCount),
        problems: String(problems.total),
        vuln: String(problems.vulnerabilities),
        risks: String(problems.risks),
        controls: String(problems.controls),
        delta: formatSignedDelta(assets.delta),
      }
    );

    const rows: Array<{
      key: string;
      title?: string;
      value?: number;
      columns?: Array<{
        label: string;
        value: number;
        valueAccent?: "problems";
      }>;
      subline?: string;
      tone: "neutral" | "critical" | "high" | "medium" | "low";
      hoverDetail: string;
    }> = [
      {
        key: "assets",
        tone: "neutral",
        columns: [
          {
            label: tr("home.metrics.informationSystems", undefined, "Information systems"),
            value: isCount,
          },
          {
            label: tr("labels.assetsTotal", undefined, "Assets"),
            value: assetsCount,
          },
          {
            label: tr("labels.problems", undefined, "Problems"),
            value: problems.total,
            valueAccent: "problems",
          },
        ],
        subline: problemsSubline,
        hoverDetail: portfolioHover,
      },
      {
        key: "critical",
        title: tr("labels.severityCritical", undefined, "Critical"),
        value: homeVulnKpis.critical,
        tone: "critical",
        hoverDetail: hoverDelta(getSeverityDelta(am, "critical")),
      },
      {
        key: "high",
        title: tr("labels.severityHigh", undefined, "High"),
        value: homeVulnKpis.high,
        tone: "high",
        hoverDetail: hoverDelta(getSeverityDelta(am, "high")),
      },
      {
        key: "medium",
        title: tr("labels.severityMedium", undefined, "Medium"),
        value: homeVulnKpis.medium,
        tone: "medium",
        hoverDetail: hoverDelta(getSeverityDelta(am, "medium")),
      },
      {
        key: "low",
        title: tr("labels.severityLow", undefined, "Low"),
        value: homeVulnKpis.low,
        tone: "low",
        hoverDetail: hoverDelta(getSeverityDelta(am, "low")),
      },
    ];
    return rows;
  }, [assetsMonitoringRaw, assets.total, assets.delta, homeVulnKpis, t, tr]);

  // --- ASSETS HEALTH DATA LOGIC ---
  const vulnPriority = VULNERABILITY_SEVERITIES;

  const aggregatedCounts = useMemo(() => {
    const totals: Record<VulnerabilitySeverity, number> = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      info: 0,
    };
    safeArray(assetsList).forEach((asset: any) => {
      vulnPriority.forEach((sev) => {
        const value = Number(asset?.[sev] ?? 0);
        if (Number.isFinite(value)) {
          totals[sev] += value;
        }
      });
    });
    return totals;
  }, [assetsList]);

  const monitoringCounts: Record<VulnerabilitySeverity, number> = {
    critical: homeVulnKpis.critical,
    high: homeVulnKpis.high,
    medium: homeVulnKpis.medium,
    low: homeVulnKpis.low,
    info: homeVulnKpis.info,
  };

  const activeAssetDetails = selectedAssetId
    ? assetDetailsCache[selectedAssetId]
    : null;
  const resolvedAssetDetails =
    activeAssetDetails?.data ?? activeAssetDetails ?? {};
  const resolvedAssetVulns =
    resolvedAssetDetails?.vulnerabilities || resolvedAssetDetails?.stats || {};
  const modalSeverityBreakdown = vulnPriority.map((sev) => ({
    key: sev,
    label: sev.charAt(0).toUpperCase() + sev.slice(1),
    count:
      Number(
        resolvedAssetVulns?.[sev] ??
          resolvedAssetVulns?.[`${sev}_count`] ??
          (selectedAssetSummary ? selectedAssetSummary[sev] : 0)
      ) || 0,
  }));

  const resolvedVulnerabilityList = Array.isArray(
    resolvedAssetDetails?.vulnerabilities?.list
  )
    ? resolvedAssetDetails.vulnerabilities.list
    : Array.isArray(resolvedAssetDetails?.vulnerabilities?.items)
    ? resolvedAssetDetails.vulnerabilities.items
    : Array.isArray(resolvedAssetDetails?.vulnerabilities)
    ? resolvedAssetDetails.vulnerabilities
    : Array.isArray(resolvedAssetDetails?.findings)
    ? resolvedAssetDetails.findings
    : [];

  const handleAssetRowClick = async (asset: any) => {
    if (!asset) return;
    const assetIdRaw =
      asset?.id ||
      asset?._id ||
      asset?.assetId ||
      asset?.uuid ||
      asset?.external_id ||
      asset?.slug;
    const assetId = assetIdRaw ? String(assetIdRaw) : null;
    setSelectedAssetSummary(asset);
    setAssetModalOpen(true);
    setAssetModalError(null);

    if (!assetId) {
      setAssetModalLoading(false);
      setSelectedAssetId(null);
      setAssetModalError("Asset identifier is unavailable");
      return;
    }

    setSelectedAssetId(assetId);

    if (assetDetailsCache[assetId]) {
      setAssetModalLoading(false);
      return;
    }

    setAssetModalLoading(true);
    try {
      const res = await fetch(
        `${API_ENDPOINTS.ASSETS}/${encodeURIComponent(assetId)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      const payload = await safeJson(res);
      if (!res.ok) {
        const message =
          payload?.error ||
          payload?.message ||
          `Failed to load asset ${assetId}`;
        throw new Error(message);
      }
      const normalizedPayload = payload?.data ?? payload;
      setAssetDetailsCache((prev) => ({
        ...prev,
        [assetId]: normalizedPayload,
      }));
    } catch (err: any) {
      console.error("[Home] asset details fetch failed:", err);
      setAssetModalError(err?.message || "Failed to load asset details");
    } finally {
      setAssetModalLoading(false);
    }
  };

  const closeAssetModal = () => {
    setAssetModalOpen(false);
    setAssetModalError(null);
    setAssetModalLoading(false);
    setSelectedAssetId(null);
    setSelectedAssetSummary(null);
  };
  // Suppliers/CustomerTrust average compliance helpers and meta
  const toArray = (src: any): any[] =>
    Array.isArray(src)
      ? src
      : Array.isArray(src?.items)
      ? src.items
      : Array.isArray(src?.data)
      ? src.data
      : Array.isArray(src?.list)
      ? src.list
      : [];
  const extractPercent = (row: any): number => {
    const v =
      row?.compliancePercentage ??
      row?.compliancePct ??
      row?.percent ??
      row?.value ??
      row?.compliance ??
      row?.compliantPct;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };
  const computeAvg = (
    src: any,
    fallback: number
  ): { avg: number; count: number } => {
    const arr = toArray(src);
    const vals = arr
      .map(extractPercent)
      .filter((n) => Number.isFinite(n)) as number[];
    const count = arr.length || 0;
    if (vals.length > 0) {
      const sum = vals.reduce((a, b) => a + b, 0);
      return { avg: Math.round(sum / vals.length), count };
    }
    const fb = Number.isFinite(Number(fallback)) ? Number(fallback) : 0;
    return { avg: Math.round(fb), count };
  };
  // Prefer detailed lists for averaging; fall back to aggregated compliantPct if lists empty
  const suppliersMeta = computeAvg(
    Array.isArray(supplierRecords) && supplierRecords.length > 0
      ? supplierRecords
      : suppliers,
    (suppliers as any)?.avgCompliance ??
      (suppliers as any)?.percent ??
      (suppliers as any)?.compliantPct ??
      0
  );
  const customersMeta = computeAvg(
    Array.isArray(customerTrustRecords) && customerTrustRecords.length > 0
      ? customerTrustRecords
      : customers,
    (customers as any)?.avgCompliance ??
      (customers as any)?.percent ??
      (customers as any)?.compliantPct ??
      0
  );
  const getGaugeColor = (p: number) =>
    p < 40 ? "#ef4444" : p < 75 ? "#facc15" : "#10b981";
  // Merge framework series into a single trend dataset by date (with fallback)
  const trendData = useMemo(() => {
    const byDate = new Map<string, any>();
    safeArray(frameworkSeries).forEach((f: any) => {
      safeArray(f?.data).forEach((pt: any) => {
        const date = String(pt?.date ?? "");
        if (!date) return;
        const row = byDate.get(date) || { date };
        row[f.framework] = Number(pt?.value ?? 0);
        byDate.set(date, row);
      });
    });
    const rows = Array.from(byDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
    if (rows.length > 0) return rows;
    const today = new Date().toISOString().slice(0, 10);
    const snapshot: Record<string, string | number> = { date: today };
    let hasValue = false;
    safeArray(complianceStandards).forEach((std: any) => {
      const code = String(std?.standardCode || std?.name || "").trim();
      if (!code) return;
      const pct = Number(std?.verifiedPercent ?? std?.selfPercent ?? std?.percent ?? 0);
      if (std?.verifiedPercent != null || std?.selfPercent != null || pct > 0) {
        snapshot[code] = Math.round(pct);
        hasValue = true;
      }
    });
    return hasValue ? [snapshot] : [];
  }, [frameworkSeries, complianceStandards]);

  // Build overall compliance trend (average across frameworks per day)
  const complianceTrend = useMemo(() => {
    try {
      const rows = Array.isArray(trendData) ? trendData : [];
      return rows.map((row: any) => {
        const keys = Object.keys(row).filter(
          (k) => k !== "date" && typeof row[k] === "number"
        );
        const avg = keys.length
          ? keys.reduce((sum, k) => sum + Number(row[k] || 0), 0) / keys.length
          : 0;
        return { date: row.date, value: avg };
      });
    } catch {
      return [] as Array<{ date: string; value: number }>;
    }
  }, [trendData]);

  // --- Security Overview metrics (must be above any early returns) ---
  const complianceScore = useMemo(() => {
    const rows = safeArray(compliance).filter(
      (s: { selfPercent?: number | null; verifiedPercent?: number | null }) =>
        s.verifiedPercent != null || s.selfPercent != null,
    );
    if (rows.length > 0) {
      const avg =
        rows.reduce(
          (
            sum: number,
            s: { selfPercent?: number | null; verifiedPercent?: number | null },
          ) => sum + Number(s.verifiedPercent ?? s.selfPercent ?? 0),
          0,
        ) / rows.length;
      return Math.round(avg);
    }
    return Number(computedHealthFromStandards ?? securityHealth?.percent ?? 0);
  }, [compliance, computedHealthFromStandards, securityHealth]);
  const assetHealthScore = useMemo(() => {
    if (!assetsMonitoring?.total) return 100;
    const critical = Number(assetsMonitoring?.critical ?? 0);
    const high = Number(assetsMonitoring?.high ?? 0);
    const medium = Number(assetsMonitoring?.medium ?? 0);
    const penalty =
      (critical * 5 + high * 3 + medium * 1) /
      Number(assetsMonitoring?.total || 1);
    return Math.max(0, 100 - penalty * 5);
  }, [assetsMonitoring]);
  const supplierTrustScore = Number(
    (suppliersMeta && suppliersMeta.avg) ??
      (suppliers as any)?.compliantPct ??
      (suppliers as any)?.percent ??
      0
  );
  const securityScore =
    (complianceScore + assetHealthScore + supplierTrustScore) / 3;
  const complianceTrendDelta = useMemo(() => {
    try {
      if (!Array.isArray(complianceTrend) || complianceTrend.length < 2)
        return 0;
      const last = Number(
        complianceTrend[complianceTrend.length - 1]?.value ?? 0
      );
      const first = Number(complianceTrend[0]?.value ?? last);
      return Math.round(last - first);
    } catch {
      return 0;
    }
  }, [complianceTrend]);
  const delta = complianceTrendDelta;

  // Visual helpers
  const getColor = (p: number) =>
    p >= 85 ? "#10b981" : p >= 40 ? "#facc15" : "#ef4444";
  // Match Compliance page palette by converting percent -> score5 (0-5)
  const getCompliancePaletteColor = (percent: number) => {
    const s5 = (Number(percent) || 0) / 20;
    if (s5 < 2) return "#EF4444";
    if (s5 < 3) return "#F59E0B";
    if (s5 < 4) return "#FBBF24";
    if (s5 >= 4) return "#10B981";
    return "#60A5FA";
  };
  const getStatus = (p: number) =>
    p >= 85
      ? tr("status.good", undefined, "Good")
      : p >= 40
      ? tr("status.needs_attention", "status.needsAttention", "Needs Attention")
      : tr("status.attention", undefined, "Attention");

  // Chart containers size diagnostics (must be before any early returns to keep hooks order stable)
  useEffect(() => {
    try {
      const el = document.querySelector(".chart-wrapper");
      if (el) {
        const r = (el as HTMLElement).getBoundingClientRect();
        // eslint-disable-next-line no-console
        console.log("[Chart Wrapper]", r.width, "x", r.height);
      }
    } catch {}
  }, []);

  if (loading)
    return (
      <div className={styles.loading}>
        Loading dashboard...
      </div>
    );

  // Diagnostic log
  try {
    console.group("[Home Dashboard Diagnostics]");
    console.log(
      JSON.stringify(
        {
          rechartsImports: {
            LineChart: typeof LineChart !== "undefined",
            ResponsiveContainer: typeof ResponsiveContainer !== "undefined",
          },
          trendData: Array.isArray(trendData) ? trendData.length : "undefined",
          hasChartsSection:
            typeof document !== "undefined"
              ? document.querySelectorAll(".ds-card h3")?.length || 0
              : 0,
        },
        null,
        2
      )
    );
    console.groupEnd();
  } catch {}

  // --- Security Overview metrics moved above to keep hooks order stable ---

  return (
    <PageLayout
      pageScope="home-page"
      className={styles.homePage}
      contentClassName={styles.content}
      title={tr("dashboard.title", "titles.securityDashboard")}
      description={<HomeDataRefreshedMeta />}
    >
      {error && (
          <div className={styles.errorBanner}>{error}</div>
        )}

      {featureFlags.STANDARD_SCOPE_VISIBILITY_V1 && restrictedMode ? (
        <p className="text-xs text-slate-600 mb-3" title={t('standardGovernance.restrictedMetricsTooltip', 'Metrics are limited to assigned standards and related systems.')}>
          {t('standardGovernance.scopedMetrics', 'Scoped metrics')} — {t('standardGovernance.restrictedMetricsTooltip', 'Metrics are limited to assigned standards and related systems.')}
        </p>
      ) : null}

      {featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
        <HomeEnterpriseOperationalCenter
          totalSecurityHealth={{
            healthPercent: healthShowPercent ? healthFinal : null,
            statusText: healthStatusText || getStatus(healthFinal),
            statusColor: healthShowPercent ? getColor(healthFinal) : '#64748b',
            compliancePercent: complianceScore,
            problems: getProblemsBreakdown(assetsMonitoringRaw),
            assessmentWarning: healthWarning,
            showPercent: healthShowPercent,
          }}
        />
      ) : featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
        <HomeExecutiveGovernanceSection />
      ) : null}

        {/* --- OVERVIEW --- 
        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-[var(--ds-text,#111827)]">
            {tr("sections.overview", "titles.overview", "Overview")}
          </h2>
          <div className="dashboard-row">
            <SecurityOverviewCard
              score={securityScore}
              delta={delta}
              compliance={complianceScore}
              assets={assetHealthScore}
              suppliers={supplierTrustScore}
            />
          </div>
        </section>
        */}

        {!featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {tr("titles.totalSecurityHealth", "titles.totalSecurityHealth", "Total Security Health")}
          </h2>
          <div className="dashboard-row">
            <SecurityCard
              size="large"
              title={tr(
                "titles.totalSecurityHealth",
                undefined,
                "Total Security Health"
              )}
              percent={healthShowPercent ? healthDisplayPercent : 0}
              color={healthShowPercent ? getColor(healthFinal) : '#94a3b8'}
              statusLabel={tr("labels.status", undefined, "Status")}
              statusText={
                healthStatusText ||
                (healthShowPercent ? getStatus(healthFinal) : 'Not assessed')
              }
              statusColor={healthShowPercent ? getColor(healthFinal) : '#64748b'}
              lastImprovedLabel={`${tr(
                "labels.last_improved",
                "labels.lastImproved",
                "Last improved"
              )}:`}
              lastImprovedText={lastImprovedStr}
              hoverDetail={
                healthWarning ||
                t(
                  "home.securityCard.hoverTotal",
                  "Vulnerability health {percent}% (penalty: Crit×4 + High×3 + Med×2 + Low×1; INFO excluded). Not an average of regulatory compliance %.",
                  {
                    percent: healthShowPercent
                      ? String(Math.round(healthFinal))
                      : 'n/a',
                    delta: String(delta),
                  }
                )
              }
              className={styles.securityCardLarge}
            />

            {!featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
              ? complianceStandards.map((std: any, idx: number) => {
              const standardName = String(std?.name ?? "—");
              const selfVerifiedSubtitle = std?.selfPercent !== undefined
                ? formatSelfVerifiedSubtitle(std, (key, fallback) => t(key, fallback))
                : null;
              // Normalize standard name to code for URL
              const normalizeStandardForUrl = (name: string): string => {
                const upper = name.toUpperCase().trim();
                if (upper.includes("GDPR")) return "GDPR";
                if (upper.includes("DORA")) return "DORA";
                if (upper.includes("NIS2") || upper.includes("NIS 2"))
                  return "NIS2";
                if (upper.includes("SOC") || upper.includes("SOC2"))
                  return "SOC 2";
                if (upper.includes("42001")) return "ISO/IEC 42001:2023";
                if (upper.includes("ISO") && upper.includes("27001"))
                  return "ISO/IEC 27001";
                if (upper.includes("PCI")) return "PCI DSS 2025";
                if (upper.includes("SWIFT")) return "SWIFT CSCF 2025";
                if (upper.includes("SDLC")) return "SDLC Security";
                if (upper.includes("TECHNICAL")) return "Technical Controls";
                if (
                  upper.includes("ENS") &&
                  (upper.includes("ES") || upper.includes("SPAIN"))
                )
                  return "ENS_ES";
                if (upper.includes("BSI")) return "BSI_DE";
                if (upper.includes("ANSSI")) return "ANSSI_FR";
                if (upper.includes("KSBP")) return "KSBP_LT";
                if (upper.includes("NCA")) return "NCA_EE";
                return name;
              };
              const standardCode = std?.standardCode
                ? String(std.standardCode)
                : normalizeStandardForUrl(standardName);
              const displayPercent =
                std?.assessmentDisplay === "not_assessed" ||
                std?.assessmentDisplay === "not_applicable"
                  ? null
                  : std?.selfPercent != null
                    ? Number(std.selfPercent)
                    : std?.verifiedPercent != null
                      ? Number(std.verifiedPercent)
                      : std?.percent != null
                        ? Number(std.percent)
                        : null;
              const pct =
                displayPercent != null ? Math.round(displayPercent) : null;
              const workflowStandardCode = normalizeComplianceStandardCode(
                std?.standardCode ? String(std.standardCode) : standardCode,
              );
              return (
                <div
                  key={String(std?.id ?? std?.name ?? idx)}
                  className={
                    featureFlags.COMPLIANCE_WORKFLOW_V1
                      ? styles.securityCardWithWorkflow
                      : undefined
                  }
                >
                  <SecurityCard
                    size="small"
                    title={standardName}
                    percent={displayPercent}
                    color={
                      displayPercent != null
                        ? getCompliancePaletteColor(displayPercent)
                        : "#94a3b8"
                    }
                    statusText={
                      std?.statusLabel ??
                      (displayPercent == null
                        ? t("home.kpi.notAssessed", "Not assessed")
                        : undefined)
                    }
                    lastImprovedText={selfVerifiedSubtitle ?? undefined}
                    hoverDetail={
                      displayPercent == null
                        ? t(
                            "home.securityCard.hoverFrameworkNotAssessed",
                            "{name}: not assessed — 0% is not implied. Total Security Health is vulnerability-based and separate.",
                            { name: standardName },
                          )
                        : selfVerifiedSubtitle
                          ? `${standardName}: ${selfVerifiedSubtitle}. ${std?.statusLabel || ""}`
                          : t(
                              "home.securityCard.hoverFramework",
                              "{name}: compliance score {percent}%.",
                              { name: standardName, percent: String(pct) },
                            )
                    }
                    className={styles.securityCardSmall}
                    onClick={() =>
                      navigate(
                        `/compliance?standard=${encodeURIComponent(standardCode)}`
                      )
                    }
                  />
                  {featureFlags.COMPLIANCE_WORKFLOW_V1 ? (
                    <div
                      className={styles.workflowCardAction}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <OperationalWorkflowButton
                        standardCode={workflowStandardCode}
                        effectiveAccess={homeEffectiveAccess}
                        size="small"
                      />
                    </div>
                  ) : null}
                </div>
              );
            })
              : null}
          </div>
        </section>
        ) : null}

        {featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
          <HomeOrganizationAssetsPanel
            informationSystemsCount={homeVulnKpis.informationSystemsCount}
            assetsCount={
              homeVulnKpis.assetsTotal > 0 ? homeVulnKpis.assetsTotal : assets.total
            }
            assetsWithVulnerabilities={homeVulnKpis.assetsWithVulnerabilities}
            openFindings={homeVulnKpis.uniqueFindings}
            problems={homeVulnKpis.problems}
            severity={{
              critical: homeVulnKpis.critical,
              high: homeVulnKpis.high,
              medium: homeVulnKpis.medium,
              low: homeVulnKpis.low,
              info: homeVulnKpis.info,
            }}
            assetsDelta={assets.delta}
            sinceLastScanDelta={
              vulnerabilityPosture?.sinceLastScan?.deltaUniqueFindings ??
              (() => {
                const raw = (assetsMonitoringRaw as Record<string, any>)?.sinceLastScan
                  ?.deltaUniqueFindings
                return typeof raw === 'number' ? raw : null
              })()
            }
            monitoringRaw={assetsMonitoringRaw}
            onNavigateMetric={(key) => {
              const routes: Record<string, string> = {
                informationSystems: '/assets?tab=information-systems',
                assetsTotal: '/assets?tab=inventory',
                assetsWithVulnerabilities: '/assets?tab=inventory&hasVulnerabilities=true',
                openFindings:
                  '/reports?view=findings&state=current',
                critical:
                  '/reports?view=findings&state=current&severity=critical',
                high:
                  '/reports?view=findings&state=current&severity=high',
                medium:
                  '/reports?view=findings&state=current&severity=medium',
                low:
                  '/reports?view=findings&state=current&severity=low',
                problems: '/compliance-center?tab=remediation',
              }
              const to = routes[key]
              if (to) navigate(to)
            }}
          />
        ) : (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {tr(
              "sections.regulatoryCompliance",
              "titles.regulatoryCompliance",
              "Regulatory Compliance"
            )}
          </h2>
          <div className={styles.sectionStack}>
            <div
              className={`${uiScope(
                "problem-cards-container"
              )} ${assetsMetricsGrid.grid}`}
            >
              {scanSummaryMetricCards.map((item) => (
                <ProblemCard
                  key={item.key}
                  {...(item.columns
                    ? { columns: item.columns, subline: item.subline }
                    : {
                        title: item.title ?? "",
                        value: item.value ?? 0,
                      })}
                  tone={item.tone}
                  hoverDetail={item.hoverDetail}
                  className={`${ASSETS_METRIC_GRID_CELL[item.key] ?? ""} ${styles.metricGridCell}`}
                />
              ))}
            </div>
          </div>
        </section>
        )}

        {featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
          <>
          {featureFlags.EXECUTIVE_POSTURE_WIDGETS_V1 ? (
            <HomeExecutivePostureWidgetsRow
              standardCodes={complianceStandards
                .map((s: { standardCode?: string; code?: string }) =>
                  String(s.standardCode || s.code || "").trim(),
                )
                .filter(Boolean)}
            />
          ) : (
            <HomeComplianceTrendPanel
              trendData={trendData}
              complianceTrend={complianceTrend}
              frameworkSeries={safeArray(frameworkSeries)}
            />
          )}
          <HomeSuppliersExecutiveCard suppliers={supplierRecords} />
          </>
        ) : (
        <section className={styles.chartsSection}>
          <div className={`ds-card ${styles.chartCard}`}>
            <h3 className={styles.chartHeading}>
              {tr(
                "titles.complianceTrend",
                undefined,
                "Compliance Trend (30 days)"
              )}
            </h3>
            <div className={`chart-wrapper ${styles.chartWrapper}`}>
              {complianceTrend.length === 0 && trendData.length === 0 ? (
                <div className={styles.chartEmpty}>
                  <div className={styles.chartEmptyInner}>
                    <div className={styles.chartEmptyLine}>No data available</div>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={
                      trendData.length > 0
                        ? trendData
                        : complianceTrend.map((pt: any) => ({
                            date: pt.date,
                            value: pt.value,
                          }))
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => `${value}%`} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#3b82f6"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>
        )}

        {!featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
          <section className="dashboard-row">
            {[
              {
                title: tr("sections.suppliers", "titles.suppliers", "Suppliers"),
                meta: suppliersMeta,
                participants:
                  (Array.isArray(supplierRecords) && supplierRecords.length) ||
                  (suppliers as any)?.total ||
                  (suppliers as any)?.count ||
                  0,
              },
            ].map((blk, i) => (
              <div key={i} className={`ds-card ${styles.supplierCard}`}>
                <div className={styles.supplierCardBody}>
                  <div className={styles.supplierTitle}>{blk.title}</div>
                  <div className={styles.supplierMeta}>
                    {tr("sections.participants", "labels.participants", "Participants")}: {blk.participants}
                  </div>
                </div>
                <div className={styles.gaugeWrap}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { value: Math.max(0, Math.min(100, blk.meta.avg)) },
                          { value: 100 - Math.max(0, Math.min(100, blk.meta.avg)) },
                        ]}
                        innerRadius={35}
                        outerRadius={45}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        isAnimationActive={false}
                      >
                        <Cell fill={getGaugeColor(blk.meta.avg)} />
                        <Cell fill="#e5e7eb" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className={styles.gaugeCenter}>{blk.meta.avg}%</div>
                </div>
              </div>
            ))}
          </section>
        ) : null}

        {/* Recommendations block removed per compliance unification - use Compliance page */}

        {/* Security Scans table removed per new design */}

        {assetModalOpen && (
          <div className={styles.modalRoot}>
            <div
              className={styles.modalBackdrop}
              onClick={closeAssetModal}
            />
            <div className={styles.modalPanel}>
              <div className={styles.modalHeader}>
                <div>
                  <h3 className={styles.modalTitle}>
                    {tr("home.assetModal.title", "Asset Vulnerabilities")}
                  </h3>
                  <div className={styles.modalSubtitle}>
                    {selectedAssetSummary?.name ||
                      resolvedAssetDetails?.name ||
                      "—"}
                  </div>
                </div>
                <Button
                  type="button"
                  color="outline"
                  size="small"
                  onClick={closeAssetModal}
                >
                  {tr("common.close", "Close")}
                </Button>
              </div>
              <div className={styles.modalBody}>
                {assetModalLoading ? (
                  <div className={styles.modalLoading}>
                    {tr("home.assetModal.loading", "Loading asset details...")}
                  </div>
                ) : assetModalError ? (
                  <div className={styles.modalError}>{assetModalError}</div>
                ) : (
                  <div className={styles.modalStack}>
                    <div className={styles.modalGrid}>
                      <div>
                        <div className={styles.modalFieldLabel}>
                          {tr("home.assetModal.assetName", "Asset Name")}
                        </div>
                        <div className={styles.modalFieldValue}>
                          {selectedAssetSummary?.name ||
                            resolvedAssetDetails?.name ||
                            "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.modalFieldLabel}>
                          {tr("home.assetModal.type", "Type")}
                        </div>
                        <div className={styles.modalFieldValueMedium}>
                          {resolvedAssetDetails?.type ||
                            selectedAssetSummary?.type ||
                            "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.modalFieldLabel}>
                          {tr("home.assetModal.environment", "Environment")}
                        </div>
                        <div className={styles.modalFieldValueMedium}>
                          {resolvedAssetDetails?.environment ||
                            resolvedAssetDetails?.env ||
                            selectedAssetSummary?.environment ||
                            "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.modalFieldLabel}>
                          {tr("home.assetModal.ipHost", "IP / Host")}
                        </div>
                        <div className={styles.modalFieldValueMedium}>
                          {resolvedAssetDetails?.ipAddress ||
                            resolvedAssetDetails?.ip ||
                            resolvedAssetDetails?.hostname ||
                            selectedAssetSummary?.ipAddress ||
                            selectedAssetSummary?.hostname ||
                            "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.modalFieldLabel}>
                          {tr(
                            "home.assetModal.lastAssessment",
                            "Last Assessment"
                          )}
                        </div>
                        <div className={styles.modalFieldValueMedium}>
                          {resolvedAssetDetails?.lastAssessment ||
                            resolvedAssetDetails?.last_scan ||
                            resolvedAssetDetails?.lastScan ||
                            "—"}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className={styles.modalSubheading}>
                        {tr(
                          "home.assetModal.severityBreakdown",
                          "Severity breakdown"
                        )}
                      </h4>
                      <div className={styles.severityGrid}>
                        {modalSeverityBreakdown.map((item) => (
                          <div
                            key={item.key}
                            className={styles.severityCell}
                          >
                            <div className={styles.severityCellLabel}>
                              {item.label}
                            </div>
                            <div
                              className={`${styles.severityCellValue} ${
                                severityColorMap[item.key as VulnerabilitySeverity] ??
                                styles.severityMuted
                              }`}
                            >
                              {item.count}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className={styles.modalSubheading}>
                        {tr(
                          "home.assetModal.vulnerabilities",
                          "Vulnerabilities"
                        )}
                      </h4>
                      {resolvedVulnerabilityList.length > 0 ? (
                        <ul className={styles.vulnList}>
                          {resolvedVulnerabilityList
                            .slice(0, 20)
                            .map((vuln: any, idx: number) => {
                              const severity = (
                                vuln?.severity ||
                                vuln?.level ||
                                vuln?.risk ||
                                ""
                              )
                                .toString()
                                .toLowerCase();
                              const colorClass =
                                (severityColorMap as Record<string, string>)[severity] ??
                                styles.severityMuted;
                              return (
                                <li
                                  key={vuln?.id || vuln?.cve || idx}
                                  className={styles.vulnItem}
                                >
                                  <div className={styles.vulnRow}>
                                    <div className={styles.vulnName}>
                                      {vuln?.title ||
                                        vuln?.name ||
                                        vuln?.cve ||
                                        `Finding ${idx + 1}`}
                                    </div>
                                    {severity && (
                                      <span
                                        className={`${styles.vulnBadge} ${colorClass}`}
                                      >
                                        {severity}
                                      </span>
                                    )}
                                  </div>
                                  {vuln?.description && (
                                    <div className={styles.vulnDesc}>
                                      {vuln.description}
                                    </div>
                                  )}
                                  {vuln?.cve && (
                                    <div className={styles.vulnCve}>
                                      {tr("home.assetModal.cve", "CVE")}:{" "}
                                      {vuln.cve}
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                        </ul>
                      ) : (
                        <div className={styles.vulnEmpty}>
                          {tr(
                            "home.assetModal.noVulnerabilities",
                            "No detailed vulnerabilities provided for this asset."
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </PageLayout>
  );
};

export default Home;
